import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import {
  DOCS, GU_FIELDS, OCCUPATIONS, RELATIONS_TO_FIRST, STATES, STEPS,
  ageOn, buyerDone, convertOnSpace, formatMobile, fullName, fullNameGu, guDigits, guPending, guSource, latinWordAt, needsGu, newBuyer,
  mobileGroups, occupationGu, replaceWord, stepProblem, withSharedAddress,
  type Buyer, type DocKey, type DocsByBuyer, type GuField, type Step,
} from './buyerFormModel';
import { shrinkIdFile } from '../../office/idUpload';
import { DOC_GUIDES, DocSketch, type SketchKind } from './DocSketch';
import './buyerForm.css';

export type BuyerFormPlace = { projectId: string; unitId: string; bookingId: string };
type Props = { mode: 'link' | 'office'; token?: string; place?: BuyerFormPlace; onExit?: () => void };
type Screen = { kind: 'welcome' } | { kind: 'step'; buyer: number; step: Step } | { kind: 'buyers' } | { kind: 'review' } | { kind: 'society' } | { kind: 'terms' } | { kind: 'otp' } | { kind: 'done' };
type FormTerms = { intro: string; items: Array<{ title: string; text: string }>; version: string };
type UnitInfo = { unitNumber: string; wing: string; projectName: string };

// Fixed Gujarati for the states people pick most; others are written by the translator.
const STATE_GU: Record<string, string> = { Gujarat: 'ગુજરાત', Maharashtra: 'મહારાષ્ટ્ર', Rajasthan: 'રાજસ્થાન', 'Madhya Pradesh': 'મધ્ય પ્રદેશ', 'Uttar Pradesh': 'ઉત્તર પ્રદેશ', Bihar: 'બિહાર', Delhi: 'દિલ્હી' };
const RELATION_GU: Record<string, string> = { son: 'પુત્ર', daughter: 'પુત્રી', wife: 'પત્ની' };

/** States from the fixed list carry their Gujarati straight away (nothing to translate or confirm). */
const withStateGu = (list: Buyer[]) => withSharedAddress(list.map((b) => (STATE_GU[b.state] && b.guFrom.state !== b.state
  ? { ...b, gu: { ...b.gu, state: STATE_GU[b.state] }, guFrom: { ...b.guFrom, state: b.state }, guOk: { ...b.guOk, state: true } }
  : b)));

const callForm = httpsCallable<any, any>(functions, 'buyerForm');
const callReview = httpsCallable<any, any>(functions, 'buyerFormReview');
const errText = (e: any) => String(e?.message || 'Something went wrong. Try again.').replace(/^Firebase(Error)?:\s*/i, '');

async function fileToUpload(file: File): Promise<{ contentType: string; dataBase64: string }> {
  const small = await shrinkIdFile(file, { minSide: 800 });
  const dataBase64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(small);
  });
  return { contentType: small.type || 'application/pdf', dataBase64 };
}

/* ── small pieces ── */
function Chips<T extends string>({ value, options, onChange, label }: { value: T | ''; options: Array<{ value: T; label: string; gu?: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div className="bfm-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="bfm-chip" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}{o.gu ? <small>{o.gu}</small> : null}
        </button>
      ))}
    </div>
  );
}

/** Edit box: other spellings to tap, and English typing that turns into Gujarati suggestions. */
function GuEditor({ source, initial, suggest, onSave, onCancel }: { source: string; initial: string; suggest: (text: string) => Promise<string[]>; onSave: (gu: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(initial);
  const [caret, setCaret] = useState(initial.length);
  const [spellings, setSpellings] = useState<string[] | null>(null);
  const [wordChoices, setWordChoices] = useState<string[] | null>(null);
  const known = useRef(new Map<string, string[]>());
  const inputRef = useRef<HTMLInputElement>(null);
  const placeCaret = useRef<number | null>(null);
  const word = latinWordAt(draft, caret);
  const wordKey = word?.word.toLowerCase() || '';

  useEffect(() => {
    let off = false;
    suggest(source).then((list) => { if (!off) setSpellings(list); });
    return () => { off = true; };
  }, [source, suggest]);

  useEffect(() => {
    if (!wordKey) { setWordChoices(null); return undefined; }
    const have = known.current.get(wordKey);
    if (have) { setWordChoices(have); return undefined; }
    setWordChoices(null);
    let off = false;
    const t = window.setTimeout(() => {
      suggest(wordKey).then((list) => {
        known.current.set(wordKey, list);
        if (!off) setWordChoices(list);
      });
    }, 250);
    return () => { off = true; window.clearTimeout(t); };
  }, [suggest, wordKey]);

  useEffect(() => {
    if (placeCaret.current == null) return;
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(placeCaret.current, placeCaret.current);
    placeCaret.current = null;
  }, [draft]);

  const apply = (next: { value: string; caret: number }) => {
    placeCaret.current = next.caret;
    setDraft(next.value);
    setCaret(next.caret);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const at = e.target.selectionStart ?? e.target.value.length;
    const converted = convertOnSpace(e.target.value, at, (w) => known.current.get(w)?.[0]);
    if (converted) { apply(converted); return; }
    setDraft(e.target.value);
    setCaret(at);
  };
  const others = (spellings || []).filter((s) => s !== draft.trim());
  const save = () => { if (draft.trim()) onSave(draft.trim()); };
  return (
    <div className="bfm-ge">
      <div className="bfm-tr">
        <span className="tag">GU</span>
        <input ref={inputRef} aria-label="Gujarati" lang="gu" autoComplete="off" autoCorrect="off" spellCheck={false} placeholder="Type in English or Gujarati" value={draft} onChange={onChange}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (word && wordChoices?.[0]) apply(replaceWord(draft, word, wordChoices[0]));
            else save();
          }} autoFocus />
      </div>
      {word ? (
        <div className="bfm-ge-row" aria-live="polite">
          <span className="bfm-hint">“{word.word}” in Gujarati:</span>
          {wordChoices === null ? <span className="bfm-hint">looking…</span> : null}
          {wordChoices?.map((s) => <button key={s} type="button" className="bfm-chip gu" onMouseDown={(e) => e.preventDefault()} onClick={() => apply(replaceWord(draft, word, s))}>{s}</button>)}
        </div>
      ) : others.length ? (
        <div className="bfm-ge-row">
          <span className="bfm-hint">Other spellings:</span>
          {others.map((s) => <button key={s} type="button" className="bfm-chip gu" onMouseDown={(e) => e.preventDefault()} onClick={() => apply({ value: s, caret: s.length })}>{s}</button>)}
        </div>
      ) : spellings === null ? <span className="bfm-hint">Looking for other spellings…</span> : null}
      {(word && wordChoices?.length === 0) || (!word && spellings?.length === 0) ? <span className="bfm-hint">Suggestions aren't available right now. You can still type Gujarati here.</span> : null}
      {word ? <span className="bfm-hint">Tap a spelling, or press space to use the first one. Anything still in English letters will print in English.</span> : <span className="bfm-hint">Not right? Type in English and pick the Gujarati spelling.</span>}
      <div className="bfm-ge-actions">
        <button type="button" className="bfm-btn sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="bfm-btn sm primary" onClick={save} disabled={!draft.trim()}>Save</button>
      </div>
    </div>
  );
}

function GuLine({ buyer, field, failed, suggest, onSet, onConfirm }: { buyer: Buyer; field: GuField; failed: boolean; suggest: (text: string) => Promise<string[]>; onSet: (gu: string) => void; onConfirm: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  if (!needsGu(buyer, field)) return null;
  const pending = guPending(buyer, field);
  const gu = buyer.gu[field] || '';
  if (editing) {
    return <GuEditor source={guSource(buyer, field)} initial={draft} suggest={suggest} onSave={(next) => { onSet(next); setEditing(false); }} onCancel={() => setEditing(false)} />;
  }
  if (pending || !gu) {
    return (
      <div className={`bfm-tr busy${failed ? ' err' : ''}`} aria-live="polite">
        <span className="tag">GU</span>
        <span className="gu">{failed ? "Couldn't write Gujarati." : 'Writing in Gujarati…'}</span>
        {failed ? <button type="button" onClick={() => { setDraft(gu); setEditing(true); }}>Type it</button> : null}
      </div>
    );
  }
  const ok = !!buyer.guOk[field];
  return (
    <div className={`bfm-tr${ok ? ' ok' : ''}`}>
      <span className="tag">{ok ? '✓' : 'GU'}</span>
      <span className="gu" lang="gu">{gu}</span>
      <button type="button" onClick={() => { setDraft(gu); setEditing(true); }}>Edit</button>
      {!ok ? <button type="button" className="ok" onClick={onConfirm} aria-label={`Gujarati for ${guSource(buyer, field)} is correct`}>✓ Correct</button> : null}
    </div>
  );
}

export function PaperBlock({ b, index }: { b: Buyer; index: number }) {
  const age = ageOn(b.dateOfBirth);
  const addr = [b.gu.addressLine1, b.gu.addressLine2, b.gu.city, b.gu.state].filter(Boolean).join(', ');
  const dash = <span className="empty">-</span>;
  return (
    <div className="bfm-paper">
      <table>
        <tbody>
          <tr><td className="n" rowSpan={4}>({guDigits(index + 1)})</td><td colSpan={3}><span className="k">નામ:</span>{fullNameGu(b) || dash}</td></tr>
          <tr>
            <td><span className="k">ઉંમર:</span>{age != null ? guDigits(age) : dash}</td>
            <td><span className="k">ધંધો:</span>{occupationGu(b) || dash}</td>
            <td><span className="k">PAN:</span><span className="en">{b.pan || b.panHint || '-'}</span></td>
          </tr>
          <tr><td colSpan={3}><span className="k">રહેવાસી:</span>{addr ? `${addr}${b.pincode ? ` - ${guDigits(b.pincode)}` : ''}` : dash}</td></tr>
          <tr>
            <td colSpan={2}><span className="k">મોબાઇલ:</span><span className="en">{b.mobile ? formatMobile(b.mobile) : '-'}</span></td>
            <td><span className="k">ઈ-મેઈલ:</span><span className="en">{b.email || '-'}</span></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** One document on the upload step: reference picture, common mistakes, and the buttons to add it. */
function DocGuide({ label, hint, kind, have, open, busy, disabled, onToggle, onFile }: {
  label: string; hint: string; kind: SketchKind; have: { url: string; isImage: boolean } | null;
  open: boolean; busy: boolean; disabled: boolean; onToggle: () => void; onFile: (file: File | undefined) => void;
}) {
  const guide = DOC_GUIDES[kind];
  const selfie = kind === 'selfie';
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => { onFile(e.target.files?.[0]); e.target.value = ''; };
  return (
    <div className={`bfm-doc${have ? ' has' : ''}`}>
      <button type="button" className="bfm-doc-h" onClick={onToggle} aria-expanded={open}>
        <span className="ico">{have && have.isImage ? <img src={have.url} alt="" /> : have ? 'PDF' : '+'}</span>
        <span className="bd"><b>{label}</b><span>{busy ? 'Uploading…' : have ? 'Uploaded' : hint}</span></span>
        <span className="act">{have ? 'Change' : open ? '' : 'Add'}</span>
      </button>
      {open ? (
        <div className="bfm-doc-b">
          {have && have.isImage ? (
            <div className="bfm-doc-now"><img src={have.url} alt={`Your ${label}`} /><span className="bfm-hint">Can you read everything clearly? If not, add it again.</span></div>
          ) : null}
          <div className="bfm-good"><DocSketch kind={kind} title={guide.good} /><span>✓ {guide.good}</span></div>
          <ul className="bfm-tips">{guide.tips.map((t) => <li key={t}>{t}</li>)}</ul>
          <div className="bfm-bads">
            {guide.mistakes.map((m) => <div key={m.flaw}><DocSketch kind={kind} flaw={m.flaw} title={m.label} /><span>✕ {m.label}</span></div>)}
          </div>
          <div className="bfm-doc-btns">
            <label className={`bfm-btn sm primary${disabled ? ' off' : ''}`}>
              {selfie ? 'Take selfie' : 'Take photo'}
              <input type="file" accept="image/*" capture={selfie ? 'user' : 'environment'} disabled={disabled} onChange={pick} />
            </label>
            <label className={`bfm-btn sm${disabled ? ' off' : ''}`}>
              {selfie ? 'Choose a photo' : 'Choose a file'}
              <input type="file" accept={selfie ? 'image/*' : 'image/*,application/pdf'} disabled={disabled} onChange={pick} />
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── the form ── */
export default function BuyerFormApp({ mode, token, place, onExit }: Props) {
  const base = useMemo(() => (mode === 'link' ? { token } : { ...place }), [mode, token, place]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState<UnitInfo | null>(null);
  const [buyers, setBuyersRaw] = useState<Buyer[]>([]);
  const [docs, setDocs] = useState<DocsByBuyer>({});
  const [status, setStatus] = useState('draft');
  const [screen, setScreen] = useState<Screen>({ kind: 'welcome' });
  const [problem, setProblem] = useState('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState('');
  const [consent, setConsent] = useState(false);
  const [checkedWithBuyer, setCheckedWithBuyer] = useState(false);
  const [society, setSociety] = useState<'yes' | 'no' | ''>('');
  const [terms, setTerms] = useState<FormTerms | null>(null);
  const [termsAgree, setTermsAgree] = useState(false);
  const [otpAvailable, setOtpAvailable] = useState(false);
  const [otpVerified, setOtpVerified] = useState<string[]>([]);
  const [otpSent, setOtpSent] = useState<string[]>([]);
  const [otpCode, setOtpCode] = useState<Record<string, string>>({});
  const [otpBusy, setOtpBusy] = useState('');
  const [sending, setSending] = useState(false);
  const [exitAsk, setExitAsk] = useState(false);
  const [uploading, setUploading] = useState<string>('');
  const [openDoc, setOpenDoc] = useState<DocKey | ''>('');
  const [guFailed, setGuFailed] = useState<Record<string, boolean>>({});
  const dirty = useRef(false);
  const verifiedFor = useRef('');
  const suggestSeen = useRef(new Map<string, Promise<string[]>>());
  const suggest = useCallback((text: string) => {
    const key = text.trim().toLowerCase();
    const seen = suggestSeen.current.get(key);
    if (seen) return seen;
    const asked = callForm({ ...base, action: 'suggest', text: key })
      .then((res) => (Array.isArray(res.data?.suggestions) ? res.data.suggestions as string[] : []))
      .catch(() => [] as string[]);
    // An empty answer may be a passing failure, so only a real answer is remembered.
    asked.then((list) => { if (!list.length) suggestSeen.current.delete(key); });
    suggestSeen.current.set(key, asked);
    return asked;
  }, [base]);
  const bodyRef = useRef<HTMLDivElement>(null);

  const setBuyers = useCallback((fn: (prev: Buyer[]) => Buyer[]) => {
    dirty.current = true;
    setBuyersRaw((prev) => withStateGu(fn(prev)));
  }, []);
  const patch = useCallback((i: number, p: Partial<Buyer>) => setBuyers((prev) => prev.map((b, k) => (k === i ? { ...b, ...p } : b))), [setBuyers]);
  const patchGu = useCallback((i: number, f: GuField, gu: string, ok: boolean) => setBuyers((prev) => prev.map((b, k) => (k === i ? {
    ...b, gu: { ...b.gu, [f]: gu }, guFrom: { ...b.guFrom, [f]: guSource(b, f) }, guOk: { ...b.guOk, [f]: ok },
  } : b))), [setBuyers]);

  // Load
  useEffect(() => {
    let off = false;
    setLoading(true);
    callForm({ ...base, action: 'load' })
      .then((res) => {
        if (off) return;
        const d = res.data || {};
        setUnit(d.unit || null);
        setBuyersRaw(withStateGu(Array.isArray(d.buyers) && d.buyers.length ? d.buyers : [{ ...newBuyer(0), mobile: String(d.prefillMobile || '') }]));
        setDocs(d.docs || {});
        setTerms(d.terms || null);
        setSociety(d.societyCommittee === 'yes' || d.societyCommittee === 'no' ? d.societyCommittee : '');
        setOtpAvailable(!!d.otpAvailable);
        setOtpVerified(Array.isArray(d.otpVerified) ? d.otpVerified : []);
        setStatus(String(d.status || 'draft'));
        if (d.status && d.status !== 'draft') setScreen({ kind: 'done' });
        if (d.updatedAtMs) setSavedAt(new Date(d.updatedAtMs));
      })
      .catch((e) => !off && setLoadError(errText(e)))
      .finally(() => !off && setLoading(false));
    return () => { off = true; };
  }, [base]);

  // Save a few seconds after the last change, so nothing is lost if the phone locks.
  useEffect(() => {
    if (!dirty.current || status !== 'draft' || loading) return undefined;
    const t = window.setTimeout(() => {
      dirty.current = false;
      callForm({ ...base, action: 'save', buyers, societyCommittee: society })
        .then(() => { setSavedAt(new Date()); setSaveError(''); })
        .catch((e) => { dirty.current = true; setSaveError(errText(e)); });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [base, buyers, loading, society, status]);

  // Gujarati: write every changed line shortly after typing stops (one call for all of them).
  const inFlight = useRef(new Set<string>());
  useEffect(() => {
    if (loading || status !== 'draft') return undefined;
    const want: Array<{ i: number; f: GuField; src: string }> = [];
    buyers.forEach((b, i) => GU_FIELDS.forEach((f) => {
      if (i > 0 && b.sameAddress && ['addressLine1', 'addressLine2', 'city', 'state'].includes(f)) return;
      if (!guPending(b, f)) return;
      const src = guSource(b, f);
      if (f === 'state' && STATE_GU[src]) return; // set directly when chosen
      if (!inFlight.current.has(src)) want.push({ i, f, src });
    }));
    if (!want.length) return undefined;
    const t = window.setTimeout(() => {
      const texts = Array.from(new Set(want.map((w) => w.src))).slice(0, 16);
      texts.forEach((s) => inFlight.current.add(s));
      callForm({ ...base, action: 'translate', texts })
        .then((res) => {
          const out: string[] = res.data?.texts || [];
          const map = new Map(texts.map((s, k) => [s, out[k] || '']));
          setGuFailed((prev) => { const next = { ...prev }; texts.forEach((s) => { next[s] = !map.get(s); }); return next; });
          setBuyers((prev) => prev.map((b) => {
            let changed = false;
            const next = { ...b, gu: { ...b.gu }, guFrom: { ...b.guFrom }, guOk: { ...b.guOk } };
            GU_FIELDS.forEach((f) => {
              const src = guSource(b, f);
              const gu = map.get(src);
              if (gu && guPending(b, f)) {
                next.gu[f] = gu;
                next.guFrom[f] = src;
                next.guOk[f] = false;
                changed = true;
              }
            });
            return changed ? next : b;
          }));
        })
        .catch(() => setGuFailed((prev) => { const next = { ...prev }; texts.forEach((s) => { next[s] = true; }); return next; }))
        .finally(() => texts.forEach((s) => inFlight.current.delete(s)));
    }, 650);
    return () => window.clearTimeout(t);
  }, [base, buyers, loading, setBuyers, status]);

  useEffect(() => { bodyRef.current?.scrollTo({ top: 0 }); setProblem(''); }, [screen]);

  // Changing any detail after verifying means verifying again (the server checks this too).
  useEffect(() => {
    if (loading) return;
    const now = JSON.stringify([buyers.map((b) => [b.firstName, b.middleName, b.lastName, b.fatherName, b.dateOfBirth, b.pan, b.panName, b.aadhaar, b.mobile, b.email, b.addressLine1, b.addressLine2, b.city, b.pincode, b.gu]), society]);
    if (verifiedFor.current && verifiedFor.current !== now) {
      setOtpVerified([]);
      setOtpSent([]);
    }
    verifiedFor.current = now;
  }, [buyers, loading, society]);

  const go = (s: Screen) => setScreen(s);
  const stepIndex = screen.kind === 'step' ? STEPS.findIndex((s) => s.key === screen.step) : -1;
  const allDone = buyers.length > 0 && buyers.every((b, i) => buyerDone(b, i, buyers));

  const next = () => {
    if (screen.kind === 'welcome') return go({ kind: 'step', buyer: 0, step: 'about' });
    if (screen.kind === 'step') {
      const p = stepProblem(buyers[screen.buyer], screen.buyer, screen.step, buyers);
      if (p) { setProblem(p); return undefined; }
      if (stepIndex < STEPS.length - 1) return go({ kind: 'step', buyer: screen.buyer, step: STEPS[stepIndex + 1].key });
      return go({ kind: 'buyers' });
    }
    if (screen.kind === 'buyers') {
      const firstOpen = buyers.findIndex((b, i) => !buyerDone(b, i, buyers));
      if (firstOpen >= 0) { setProblem(`Buyer ${firstOpen + 1} still has details missing. Tap Edit to finish.`); return undefined; }
      return go({ kind: 'review' });
    }
    if (screen.kind === 'review') {
      if (!consent) { setProblem('Tick the box to confirm the details are correct.'); return undefined; }
      return go({ kind: 'society' });
    }
    if (screen.kind === 'society') {
      if (!society) { setProblem('Choose Yes or No.'); return undefined; }
      return go({ kind: 'terms' });
    }
    if (screen.kind === 'terms') {
      if (!terms) { setProblem('The terms could not be loaded. Ask the Swarna office for help.'); return undefined; }
      if (!termsAgree) { setProblem('Tick the box to agree to the terms and conditions.'); return undefined; }
      return go({ kind: 'otp' });
    }
    return undefined;
  };
  const back = () => {
    if (screen.kind === 'step') {
      if (stepIndex > 0) return go({ kind: 'step', buyer: screen.buyer, step: STEPS[stepIndex - 1].key });
      return go(screen.buyer === 0 ? { kind: 'welcome' } : { kind: 'buyers' });
    }
    if (screen.kind === 'buyers') return go({ kind: 'step', buyer: buyers.length - 1, step: 'docs' });
    if (screen.kind === 'review') return go({ kind: 'buyers' });
    if (screen.kind === 'society') return go({ kind: 'review' });
    if (screen.kind === 'terms') return go({ kind: 'society' });
    if (screen.kind === 'otp') return go({ kind: 'terms' });
    return undefined;
  };

  const agreement = () => ({ buyers, consent: true, societyCommittee: society, termsAccepted: termsAgree, termsVersion: terms?.version });
  const sendCode = async (mobile: string) => {
    setOtpBusy(mobile);
    setProblem('');
    try {
      await callForm({ ...base, action: 'otpSend', mobile, ...agreement() });
      dirty.current = false;
      setOtpSent((prev) => (prev.includes(mobile) ? prev : [...prev, mobile]));
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setOtpBusy('');
    }
  };
  const verifyCode = async (mobile: string) => {
    setOtpBusy(mobile);
    setProblem('');
    try {
      await callForm({ ...base, action: 'otpVerify', mobile, code: otpCode[mobile] || '' });
      setOtpVerified((prev) => [...prev, mobile]);
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setOtpBusy('');
    }
  };

  const send = async () => {
    if (mode === 'office' && !checkedWithBuyer) { setProblem('Tick that you checked the details with the buyer.'); return; }
    setSending(true);
    setProblem('');
    try {
      await callForm({ ...base, action: 'submit', ...agreement(), checkedWithBuyer });
      dirty.current = false;
      if (mode === 'office') await callReview({ ...place, decision: 'approve' });
      setStatus(mode === 'office' ? 'approved' : 'submitted');
      go({ kind: 'done' });
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setSending(false);
    }
  };

  const upload = async (i: number, key: DocKey, file: File | undefined) => {
    if (!file) return;
    const b = buyers[i];
    setUploading(`${b.key}:${key}`);
    setProblem('');
    try {
      const ready = await fileToUpload(file);
      const res = await callForm({ ...base, action: 'upload', buyerKey: b.key, docKey: key, ...ready });
      setDocs((prev) => ({ ...prev, [b.key]: { ...(prev[b.key] || {}), [key]: res.data?.doc } }));
      // Make sure the buyer row exists on the server before the file is tied to it.
      dirty.current = true;
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setUploading('');
    }
  };

  const addBuyer = () => {
    setBuyers((prev) => [...prev, newBuyer(prev.length)]);
    go({ kind: 'step', buyer: buyers.length, step: 'about' });
  };
  const move = (i: number, dir: -1 | 1) => setBuyers((prev) => {
    const next = [...prev];
    const j = i + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]];
    return next.map((b, k) => ({ ...b, sameAddress: k > 0 && b.sameAddress, relationToFirst: k > 0 ? b.relationToFirst : '' }));
  });
  const remove = (i: number) => setBuyers((prev) => prev.filter((_, k) => k !== i));

  /* ── screens ── */
  if (loading) return <div className="bfm"><div className="bfm-center"><div><p className="muted">Opening the form…</p></div></div></div>;
  if (loadError) {
    return (
      <div className="bfm">
        <div className="bfm-center"><div>
          <h1>This form can't be opened</h1>
          <p className="muted">{loadError}</p>
          {onExit ? <button className="bfm-btn" onClick={onExit}>Back to the unit</button> : null}
        </div></div>
      </div>
    );
  }

  const flat = unit ? `Flat ${unit.unitNumber}${unit.wing ? `, ${unit.wing}` : ''}` : '';
  const b = screen.kind === 'step' ? buyers[screen.buyer] : null;
  const bi = screen.kind === 'step' ? screen.buyer : 0;
  const guProps = (f: GuField) => ({
    buyer: b!, field: f, failed: !!guFailed[guSource(b!, f)], suggest,
    onSet: (gu: string) => patchGu(bi, f, gu, true), onConfirm: () => patchGu(bi, f, b!.gu[f] || '', true),
  });
  const head = (() => {
    if (screen.kind === 'welcome') return { title: 'Buyer details', sub: unit ? `${flat} · ${unit.projectName}` : '' };
    if (screen.kind === 'step') return { title: STEPS[stepIndex].title, sub: `Buyer ${bi + 1}${fullName(b!) ? ` · ${fullName(b!)}` : ''}` };
    if (screen.kind === 'buyers') return { title: 'All buyers', sub: 'The order here is the order on the booking form and the agreement.' };
    if (screen.kind === 'review') return { title: 'Check the details', sub: 'This is how the details will print. Tap Edit on a buyer to fix anything.' };
    if (screen.kind === 'society') return { title: 'Society committee', sub: 'One answer for this booking.' };
    if (screen.kind === 'terms') return { title: 'Terms and conditions', sub: 'Please read these. They also print on your booking form.' };
    if (screen.kind === 'otp') return { title: 'Verify and send', sub: otpAvailable ? 'We send a code on WhatsApp to each mobile number on this form.' : 'Last step.' };
    return { title: '', sub: '' };
  })();

  return (
    <div className="bfm">
      <header className="bfm-top">
        <div className="bfm-brand">SWARNA GROUP<span>{unit?.projectName || ''}</span></div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {flat ? <span className="bfm-pill">{flat}</span> : null}
          {mode === 'office' ? <button className="bfm-exit" onClick={() => setExitAsk(true)}>Exit buyer mode</button> : null}
        </div>
      </header>

      {screen.kind === 'done' ? (
        <div className="bfm-center"><div>
          <span className="bfm-tick" aria-hidden="true">✓</span>
          <h1>{status === 'approved' ? 'Details saved' : 'Thank you'}</h1>
          <p className="muted">
            {status === 'approved'
              ? mode === 'office' ? 'The buyer details are approved and saved on the booking.' : 'The Swarna office has approved your details.'
              : 'Your details were sent to the Swarna office. They will call you if anything needs changing.'}
          </p>
          {onExit ? <button className="bfm-btn primary" onClick={onExit}>Back to the unit</button> : null}
        </div></div>
      ) : (
        <div className="bfm-grid">
          <nav className="bfm-side" aria-label="Steps">
            <div className="who"><b>{flat || 'Your booking'}</b><span>{unit?.projectName}</span></div>
            {buyers.map((x, i) => (
              <React.Fragment key={x.key}>
                <div className="bfm-sb">Buyer {i + 1}{fullName(x) ? ` · ${x.firstName}` : ''}</div>
                {STEPS.map((s, k) => {
                  const on = screen.kind === 'step' && screen.buyer === i && screen.step === s.key;
                  const done = !stepProblem(x, i, s.key, buyers);
                  return (
                    <button key={s.key} className={`bfm-ss${on ? ' on' : ''}${done && !on ? ' done' : ''}`} onClick={() => go({ kind: 'step', buyer: i, step: s.key })} aria-current={on ? 'step' : undefined}>
                      <i>{done && !on ? '✓' : k + 1}</i>{s.title}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
            <div className="bfm-sb">Finish</div>
            <button className={`bfm-ss${screen.kind === 'buyers' ? ' on' : ''}`} onClick={() => go({ kind: 'buyers' })}><i>+</i>All buyers</button>
            <button className={`bfm-ss${screen.kind === 'review' ? ' on' : ''}`} onClick={() => (allDone ? go({ kind: 'review' }) : go({ kind: 'buyers' }))}><i>✓</i>Check the details</button>
            <button className={`bfm-ss${screen.kind === 'society' ? ' on' : ''}`} disabled={!allDone || !consent} onClick={() => go({ kind: 'society' })}><i>+</i>Society committee</button>
            <button className={`bfm-ss${screen.kind === 'terms' ? ' on' : ''}`} disabled={!allDone || !consent || !society} onClick={() => go({ kind: 'terms' })}><i>+</i>Terms and conditions</button>
            <button className={`bfm-ss${screen.kind === 'otp' ? ' on' : ''}`} disabled={!allDone || !consent || !society || !termsAgree} onClick={() => go({ kind: 'otp' })}><i>✓</i>Verify and send</button>
          </nav>

          <main className="bfm-main">
            <div className="bfm-head">
              <h1>{head.title}</h1>
              {head.sub ? <p>{head.sub}</p> : null}
              {screen.kind === 'step' ? <div className="bfm-prog" aria-hidden="true">{STEPS.map((s, k) => <i key={s.key} className={k <= stepIndex ? 'on' : ''} />)}</div> : null}
            </div>
            <div className="bfm-body" ref={bodyRef}>
              {screen.kind === 'welcome' ? (
                <>
                  <div className="bfm-hero"><span>Booking details for</span><b>{flat}</b><span>{unit?.projectName}</span></div>
                  <p style={{ margin: 0 }}>Fill in the details of every buyer in English. Under each line we show the Gujarati as it will print on your booking form, so you can check it.</p>
                  <span className="bfm-sec">Keep these ready</span>
                  <ul className="bfm-list">
                    <li><i>1</i>PAN card of each buyer</li>
                    <li><i>2</i>Aadhaar card of each buyer</li>
                    <li><i>3</i>A recent photo of each buyer</li>
                  </ul>
                  <span className="bfm-hint">About 5 minutes per buyer. Your progress is saved, so you can stop and open this link again later.</span>
                </>
              ) : null}

              {screen.kind === 'step' && screen.step === 'about' && b ? (
                <>
                  <div className="bfm-f">
                    <label htmlFor="bf-fn">First name <span className="gu">નામ</span></label>
                    <input id="bf-fn" className="bfm-inp" autoCapitalize="words" autoComplete="given-name" value={b.firstName} onChange={(e) => patch(bi, { firstName: e.target.value })} />
                    <GuLine {...guProps('firstName')} />
                  </div>
                  <div className="bfm-g2">
                    <div className="bfm-f">
                      <label htmlFor="bf-mn">Middle name</label>
                      <input id="bf-mn" className="bfm-inp" autoCapitalize="words" value={b.middleName} onChange={(e) => patch(bi, { middleName: e.target.value })} />
                      <GuLine {...guProps('middleName')} />
                    </div>
                    <div className="bfm-f">
                      <label htmlFor="bf-ln">Surname</label>
                      <input id="bf-ln" className="bfm-inp" autoCapitalize="words" autoComplete="family-name" value={b.lastName} onChange={(e) => patch(bi, { lastName: e.target.value })} />
                      <GuLine {...guProps('lastName')} />
                    </div>
                  </div>
                  <span className="bfm-hint">Write the name as you want it on the booking form, agreement and sale deed.</span>
                  <div className="bfm-f">
                    <span className="lbl">Father's or husband's name</span>
                    <Chips label="Relation" value={b.fatherRelation} onChange={(v) => patch(bi, { fatherRelation: v })}
                      options={[{ value: 'son', label: 'Son of', gu: RELATION_GU.son }, { value: 'daughter', label: 'Daughter of', gu: RELATION_GU.daughter }, { value: 'wife', label: 'Wife of', gu: RELATION_GU.wife }]} />
                    <input id="bf-fa" className="bfm-inp" aria-label="Father's or husband's full name" placeholder="Full name" autoCapitalize="words" value={b.fatherName} onChange={(e) => patch(bi, { fatherName: e.target.value })} />
                    <span className="bfm-hint">Write the full name, even if your ID shows a short form.</span>
                    <GuLine {...guProps('fatherName')} />
                  </div>
                  <div className="bfm-g2">
                    <div className="bfm-f">
                      <label htmlFor="bf-dob">Date of birth</label>
                      <input id="bf-dob" className="bfm-inp num" type="date" max={new Date().toISOString().slice(0, 10)} value={b.dateOfBirth} onChange={(e) => patch(bi, { dateOfBirth: e.target.value })} />
                    </div>
                    <div className="bfm-f">
                      <span className="lbl">Age on form</span>
                      <div className="bfm-inp ro num">{ageOn(b.dateOfBirth) != null ? <>{ageOn(b.dateOfBirth)} <span className="gu">· {guDigits(ageOn(b.dateOfBirth)!)} વર્ષ</span></> : '-'}</div>
                    </div>
                  </div>
                  <div className="bfm-f">
                    <span className="lbl">Gender</span>
                    <Chips label="Gender" value={b.gender} onChange={(v) => patch(bi, { gender: v })} options={[{ value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }]} />
                  </div>
                </>
              ) : null}

              {screen.kind === 'step' && screen.step === 'work' && b ? (
                <>
                  <div className="bfm-f">
                    <span className="lbl">Occupation <span className="gu">ધંધો</span></span>
                    <Chips label="Occupation" value={b.occupation} onChange={(v) => patch(bi, { occupation: v })}
                      options={OCCUPATIONS.map((o) => ({ value: o.value, label: o.label, gu: o.gu }))} />
                    {b.occupation === 'Other' ? (
                      <>
                        <input className="bfm-inp" aria-label="Occupation" placeholder="Write your occupation" value={b.occupationOther} onChange={(e) => patch(bi, { occupationOther: e.target.value })} />
                        <GuLine {...guProps('occupationOther')} />
                      </>
                    ) : null}
                  </div>
                  <div className="bfm-f">
                    <span className="lbl">Living in</span>
                    <Chips label="Living in" value={b.residentStatus} onChange={(v) => patch(bi, { residentStatus: v })} options={[{ value: 'india', label: 'India' }, { value: 'nri', label: 'Outside India (NRI)' }]} />
                    <span className="bfm-hint">This decides the TDS rule on your payments.</span>
                  </div>
                  <div className="bfm-g2">
                    <div className="bfm-f">
                      <label htmlFor="bf-pan">PAN</label>
                      <input id="bf-pan" className="bfm-inp upper num" maxLength={10} autoCapitalize="characters" placeholder={b.panHint ? `Saved: ${b.panHint}` : 'ABCDE1234F'} value={b.pan} onChange={(e) => patch(bi, { pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
                      {b.panHint && !b.pan ? <span className="bfm-hint">Saved already. Leave empty to keep it.</span> : null}
                    </div>
                    <div className="bfm-f">
                      <label htmlFor="bf-aad">Aadhaar number</label>
                      <input id="bf-aad" className="bfm-inp num" inputMode="numeric" maxLength={14} placeholder={b.aadhaarHint ? `Saved: ${b.aadhaarHint}` : '12 digits'} value={b.aadhaar.replace(/(\d{4})(?=\d)/g, '$1 ')} onChange={(e) => patch(bi, { aadhaar: e.target.value.replace(/\D/g, '').slice(0, 12) })} />
                      <span className="bfm-hint">{b.aadhaarHint && !b.aadhaar ? 'Saved already. Leave empty to keep it.' : 'Needed to register the sale deed. Only the office sees it.'}</span>
                    </div>
                  </div>
                  <div className="bfm-f">
                    <span className="lbl">Name on PAN card</span>
                    <Chips label="Name on PAN card" value={b.panNameMode} onChange={(v) => patch(bi, { panNameMode: v })}
                      options={[{ value: 'same', label: fullName(b) ? `Same as: ${fullName(b)}` : 'Same as my name' }, { value: 'different', label: 'Different name on PAN' }]} />
                    {b.panNameMode === 'different' ? (
                      <input className="bfm-inp" aria-label="Name exactly as on the PAN card" placeholder="Exactly as on the PAN card" autoCapitalize="characters" value={b.panName} onChange={(e) => patch(bi, { panName: e.target.value })} />
                    ) : null}
                    <span className="bfm-hint">Look at your PAN card. It can be different from the name you want on documents.</span>
                  </div>
                  <div className="bfm-f">
                    <label htmlFor="bf-mob">Mobile</label>
                    <input id="bf-mob" className="bfm-inp num" inputMode="tel" autoComplete="tel-national" maxLength={10} placeholder="10 digits" value={b.mobile} onChange={(e) => patch(bi, { mobile: e.target.value.replace(/\D/g, '').slice(-10) })} />
                  </div>
                  <label className="bfm-check"><input type="checkbox" checked={b.whatsappSame} onChange={(e) => patch(bi, { whatsappSame: e.target.checked })} />Same number on WhatsApp</label>
                  {!b.whatsappSame ? (
                    <div className="bfm-f">
                      <label htmlFor="bf-wa">WhatsApp number</label>
                      <input id="bf-wa" className="bfm-inp num" inputMode="tel" maxLength={10} value={b.whatsapp} onChange={(e) => patch(bi, { whatsapp: e.target.value.replace(/\D/g, '').slice(-10) })} />
                    </div>
                  ) : null}
                  <div className="bfm-f">
                    <label htmlFor="bf-em">Email <span className="gu">optional</span></label>
                    <input id="bf-em" className="bfm-inp" type="email" autoComplete="email" value={b.email} onChange={(e) => patch(bi, { email: e.target.value.trim() })} />
                  </div>
                </>
              ) : null}

              {screen.kind === 'step' && screen.step === 'address' && b ? (
                <>
                  {bi > 0 ? (
                    <>
                      <div className="bfm-f">
                        <span className="lbl">Relation to buyer 1 ({buyers[0].firstName || 'buyer 1'})</span>
                        <Chips label="Relation to buyer 1" value={b.relationToFirst} onChange={(v) => patch(bi, { relationToFirst: v })} options={RELATIONS_TO_FIRST.map((r) => ({ value: r, label: r }))} />
                      </div>
                      <label className="bfm-check"><input type="checkbox" checked={b.sameAddress} onChange={(e) => patch(bi, { sameAddress: e.target.checked })} />Lives at the same address as buyer 1</label>
                    </>
                  ) : null}
                  {bi > 0 && b.sameAddress ? (
                    <PaperBlock b={b} index={bi} />
                  ) : (
                    <>
                      <div className="bfm-f">
                        <label htmlFor="bf-a1">House / flat and building <span className="gu">રહેવાસી</span></label>
                        <input id="bf-a1" className="bfm-inp" autoComplete="address-line1" value={b.addressLine1} onChange={(e) => patch(bi, { addressLine1: e.target.value })} />
                        <GuLine {...guProps('addressLine1')} />
                      </div>
                      <div className="bfm-f">
                        <label htmlFor="bf-a2">Street and area</label>
                        <input id="bf-a2" className="bfm-inp" autoComplete="address-line2" value={b.addressLine2} onChange={(e) => patch(bi, { addressLine2: e.target.value })} />
                        <GuLine {...guProps('addressLine2')} />
                      </div>
                      <div className="bfm-g2">
                        <div className="bfm-f">
                          <label htmlFor="bf-ct">City</label>
                          <input id="bf-ct" className="bfm-inp" autoComplete="address-level2" value={b.city} onChange={(e) => patch(bi, { city: e.target.value })} />
                          <GuLine {...guProps('city')} />
                        </div>
                        <div className="bfm-f">
                          <label htmlFor="bf-pin">Pincode</label>
                          <input id="bf-pin" className="bfm-inp num" inputMode="numeric" autoComplete="postal-code" maxLength={6} value={b.pincode} onChange={(e) => patch(bi, { pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
                        </div>
                      </div>
                      <div className="bfm-f">
                        <label htmlFor="bf-st">State</label>
                        <select id="bf-st" className="bfm-inp" value={b.state} onChange={(e) => {
                          const st = e.target.value;
                          setBuyers((prev) => prev.map((x, k) => (k !== bi ? x : STATE_GU[st]
                            ? { ...x, state: st, gu: { ...x.gu, state: STATE_GU[st] }, guFrom: { ...x.guFrom, state: st }, guOk: { ...x.guOk, state: true } }
                            : { ...x, state: st })));
                        }}>
                          {STATES.map((s) => <option key={s} value={s}>{STATE_GU[s] ? `${s} · ${STATE_GU[s]}` : s}</option>)}
                        </select>
                        {!STATE_GU[b.state] ? <GuLine {...guProps('state')} /> : null}
                      </div>
                    </>
                  )}
                </>
              ) : null}

              {screen.kind === 'step' && screen.step === 'docs' && b ? (
                <>
                  <span className="bfm-hint">One at a time. Follow the picture so the office doesn't have to ask you again.</span>
                  {DOCS.map((d) => {
                    const have = docs[b.key]?.[d.key];
                    const open = (openDoc || DOCS.find((x) => !docs[b.key]?.[x.key])?.key) === d.key;
                    return (
                      <DocGuide
                        key={d.key}
                        label={d.label}
                        hint={d.hint}
                        kind={(d.key === 'photo' ? 'selfie' : d.key) as SketchKind}
                        have={have ? { url: have.url, isImage: have.contentType.startsWith('image/') } : null}
                        open={open}
                        busy={uploading === `${b.key}:${d.key}`}
                        disabled={!!uploading}
                        onToggle={() => setOpenDoc(open ? ('' as any) : d.key)}
                        onFile={(file) => { setOpenDoc(''); upload(bi, d.key, file); }}
                      />
                    );
                  })}
                  <span className="bfm-hint">These go to the office's KYC file and never print on the form. {mode === 'office' ? 'You can skip any that the office will scan later.' : 'If you do not have one now, continue; the office can collect it later.'}</span>
                </>
              ) : null}

              {screen.kind === 'buyers' ? (
                <>
                  {buyers.map((x, i) => {
                    const done = buyerDone(x, i, buyers);
                    return (
                      <div className="bfm-bi" key={x.key}>
                        <span className="mv">
                          <button aria-label={`Move buyer ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                          <button aria-label={`Move buyer ${i + 1} down`} disabled={i === buyers.length - 1} onClick={() => move(i, 1)}>▼</button>
                        </span>
                        <span className="n">{guDigits(i + 1)}</span>
                        <span className="bd"><b>{fullName(x) || 'Name not filled'}</b><span>{i === 0 ? 'Main applicant' : x.relationToFirst ? `${x.relationToFirst} of buyer 1` : 'Co-buyer'}</span></span>
                        <span className={`st ${done ? 'done' : 'todo'}`}>{done ? 'Done' : 'Missing details'}</span>
                        <span className="acts">
                          <button className="bfm-btn sm" onClick={() => go({ kind: 'step', buyer: i, step: 'about' })}>Edit</button>
                          {buyers.length > 1 && (mode === 'office' || !x.customerId) ? <button className="bfm-btn sm danger" onClick={() => remove(i)}>Remove</button> : null}
                        </span>
                      </div>
                    );
                  })}
                  <button className="bfm-btn dashed" onClick={addBuyer} disabled={buyers.length >= 12}>+ Add another buyer</button>
                </>
              ) : null}

              {screen.kind === 'review' ? (
                <>
                  {buyers.map((x, i) => (
                    <div key={x.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="bfm-sec">Buyer {i + 1}</span>
                        <button className="bfm-btn sm" onClick={() => go({ kind: 'step', buyer: i, step: 'about' })}>Edit</button>
                      </div>
                      <PaperBlock b={x} index={i} />
                      <span className="bfm-hint gu">{x.fatherRelation ? `${RELATION_GU[x.fatherRelation]}: ` : ''}{x.gu.fatherName}</span>
                      <span className="bfm-hint">Name on PAN card: {x.panNameMode === 'same' ? fullName(x) : x.panName}</span>
                    </div>
                  ))}
                  <label className="bfm-check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />I have checked the English and Gujarati details of every buyer. They are correct, and Swarna Infra may use them for this booking.</label>
                </>
              ) : null}

              {screen.kind === 'society' ? (
                <div className="bfm-f">
                  <span className="lbl">Would you like to take part in the society committee? <span className="gu">શું તમે સોસાયટી સમિતિમાં ભાગ લેવા ઈચ્છો છો?</span></span>
                  <Chips label="Society committee" value={society} onChange={(v) => setSociety(v)} options={[{ value: 'yes', label: 'Yes', gu: 'હા' }, { value: 'no', label: 'No', gu: 'ના' }]} />
                </div>
              ) : null}

              {screen.kind === 'terms' ? (
                terms ? (
                  <>
                    <div className="bfm-terms gu" lang="gu">
                      <p>{terms.intro}</p>
                      {terms.items.map((t) => <p key={t.title}><b>{t.title}:</b> {t.text}</p>)}
                    </div>
                    <label className="bfm-check"><input type="checkbox" checked={termsAgree} onChange={(e) => setTermsAgree(e.target.checked)} />I have read these terms and conditions, and I agree to them on behalf of all buyers on this form.</label>
                  </>
                ) : <div className="bfm-problem" role="alert">The terms could not be loaded. Ask the Swarna office for help.</div>
              ) : null}

              {screen.kind === 'otp' ? (
                <>
                  {otpAvailable ? mobileGroups(buyers).map((g) => {
                    const done = otpVerified.includes(g.mobile);
                    const sent = otpSent.includes(g.mobile);
                    const busy = otpBusy === g.mobile;
                    return (
                      <div className="bfm-otp" key={g.mobile}>
                        <div className="bfm-otp-h"><span><b className="num">{formatMobile(g.mobile)}</b><span className="bfm-hint"> {g.names.join(', ')}</span></span>{done ? <span className="bfm-otp-ok">✓ Verified</span> : null}</div>
                        {!done ? (
                          <div className="bfm-otp-row">
                            {sent ? <input className="bfm-inp num" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code" aria-label={`Code sent to ${g.mobile}`} value={otpCode[g.mobile] || ''} onChange={(e) => setOtpCode((prev) => ({ ...prev, [g.mobile]: e.target.value.replace(/\D/g, '').slice(0, 6) }))} /> : null}
                            {sent ? <button className="bfm-btn sm primary" disabled={busy || (otpCode[g.mobile] || '').length !== 6} onClick={() => verifyCode(g.mobile)}>{busy ? 'Checking…' : 'Verify'}</button> : null}
                            <button className="bfm-btn sm" disabled={busy} onClick={() => sendCode(g.mobile)}>{busy && !sent ? 'Sending…' : sent ? 'Send again' : 'Send code on WhatsApp'}</button>
                          </div>
                        ) : null}
                      </div>
                    );
                  }) : <p style={{ margin: 0 }}>{mode === 'office' ? 'Verification by code is not switched on yet.' : 'The Swarna office will confirm these details with you.'}</p>}
                  {mode === 'office' ? <label className="bfm-check"><input type="checkbox" checked={checkedWithBuyer} onChange={(e) => setCheckedWithBuyer(e.target.checked)} />Staff: I checked these details with the buyer present.</label> : null}
                </>
              ) : null}
            </div>
            <div className="bfm-foot">
              {problem ? <div className="bfm-problem" role="alert">{problem}</div> : null}
              <div className="bfm-foot-row">
                <span className="bfm-saved">{saveError ? `Not saved: ${saveError}` : savedAt ? `Saved ${savedAt.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}` : ''}</span>
                {screen.kind !== 'welcome' ? <button className="bfm-btn" onClick={back}>Back</button> : null}
                {screen.kind === 'otp' ? (
                  <button className="bfm-btn primary" onClick={send} disabled={sending || (mode === 'link' && otpAvailable && mobileGroups(buyers).some((g) => !otpVerified.includes(g.mobile)))}>{sending ? 'Sending…' : mode === 'office' ? 'Save and approve' : 'Send to Swarna office'}</button>
                ) : (
                  <button className="bfm-btn primary" onClick={next}>
                    {screen.kind === 'welcome' ? 'Start' : screen.kind === 'buyers' ? 'Check the details' : screen.kind === 'step' && stepIndex === STEPS.length - 1 ? `Save buyer ${bi + 1}` : 'Next'}
                  </button>
                )}
              </div>
            </div>
          </main>

          <aside className="bfm-prev" aria-label="Booking form preview">
            <span className="bfm-sec">Your booking form, live</span>
            <div className="bfm-paper" style={{ padding: '8px 10px' }}><h4>બુકિંગ ફોર્મ · {unit?.projectName} · {unit?.unitNumber}</h4></div>
            {buyers.map((x, i) => <PaperBlock key={x.key} b={x} index={i} />)}
            <span className="bfm-hint">Father's name, Aadhaar and NRI status are saved for the agreement and the sale deed; they do not print on the booking form.</span>
          </aside>
        </div>
      )}

      {exitAsk ? (
        <div className="bfm-modal" role="dialog" aria-modal="true" aria-labelledby="bfm-exit-t">
          <div>
            <h2 id="bfm-exit-t">Hand back to staff?</h2>
            <p className="muted" style={{ margin: 0 }}>What was typed is saved. Staff can open the form again from the unit page.</p>
            <div className="row">
              <button className="bfm-btn" onClick={() => setExitAsk(false)}>Keep filling</button>
              <button className="bfm-btn primary" onClick={async () => {
                setExitAsk(false);
                if (dirty.current && status === 'draft') {
                  dirty.current = false;
                  await callForm({ ...base, action: 'save', buyers }).catch(() => undefined);
                }
                onExit?.();
              }}>Exit</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
